import { styled } from '@linaria/react';
import { ApprovalMode, ToolLoadingMode } from '../../../../../api/agents-client/agent-client.type';
import { useFeatureFlag } from '../../../../../helpers/feature-flags/feature-flags.hooks';
import { FeatureFlag } from '../../../../../helpers/feature-flags/feature-flags.type';
import { Button } from '../../../../../ui-library/button/button';
import { Icon } from '../../../../../ui-library/icon/icon';
import { APPROVAL_MODE_OPTIONS, TOOL_LOADING_OPTIONS } from '../../agent-customize/agent-customize.constants';
import { RadioCardGroup } from '../../shared/settings/radio-card-group';
import { SaveStatus } from '../../shared/settings/save-status';
import { SettingsCard, SettingsCardTitle } from '../../shared/settings/settings-card';
import type { RadioCardOption } from '../../shared/settings/settings.type';
import { FloraSettingsSection } from '../agent-settings.type';
import { useFloraToolSettings } from '../use-flora-tool-settings';
import { SettingsSectionShell } from './settings-section-shell';

/** Icons for the Ask / Auto cards, matching the chat composer's mode menu. */
const APPROVAL_MODE_ICONS: Record<ApprovalMode, string> = {
  [ApprovalMode.Ask]: 'front_hand',
  [ApprovalMode.Auto]: 'bolt',
};

const TOOL_LOADING_ICONS: Record<ToolLoadingMode, string> = {
  [ToolLoadingMode.OnDemand]: 'manage_search',
  [ToolLoadingMode.All]: 'download',
};

const APPROVAL_MODE_CARDS: RadioCardOption<ApprovalMode>[] = APPROVAL_MODE_OPTIONS.map((option) => ({
  value: option.mode,
  label: option.label,
  description: option.description,
  icon: APPROVAL_MODE_ICONS[option.mode],
}));

const TOOL_LOADING_CARDS: RadioCardOption<ToolLoadingMode>[] = TOOL_LOADING_OPTIONS.map((option) => ({
  value: option.mode,
  label: option.label,
  description: option.description,
  icon: TOOL_LOADING_ICONS[option.mode],
  badge: option.recommended ? 'Recommended' : undefined,
}));

/**
 * Renders the General settings section:
 * 1. The default Ask / Auto approval mode (when the organization has Auto mode)
 * 2. How Flora loads its tools when a chat starts
 * 3. A confirmed reset of every tool and approval to Flora's defaults
 *
 * Changes save automatically.
 *
 * @returns {JSX.Element} The General section
 */
export function SettingsGeneralSection() {
  const isAutoModeEnabled = useFeatureFlag(FeatureFlag.FloraAutoMode);
  const { draft, isLoading, error, saveState, setApprovalMode, setToolLoading, resetToDefaults } =
    useFloraToolSettings();

  const handleReset = () => {
    // Settings save as they change, so a reset can't be undone with a Save/Cancel.
    if (
      !window.confirm(
        "Reset Flora's tools and approvals to their defaults? Approval mode, tool loading, and every tool on Tools and Connections go back to how Flora ships. Your profile and memory settings stay as they are.",
      )
    )
      return;
    resetToDefaults();
  };

  return (
    <SettingsSectionShell
      section={FloraSettingsSection.General}
      subtitle="How Flora works in every new chat. Turn individual tools on or off on Tools and Connections."
      headerRight={<SaveStatus state={saveState} />}
      isLoading={isLoading}
      errorMessage={error || (!isLoading && !draft) ? 'Failed to load Flora settings' : null}
    >
      {draft && (
        <>
          {isAutoModeEnabled && (
            <SettingsCard aria-labelledby="settings-approval-mode">
              <SettingsCardTitle
                id="settings-approval-mode"
                title="Approval mode"
                description="The default for new chats. Switching in the composer changes only that chat."
              />
              <CardBody>
                <RadioCardGroup
                  label="Approval mode"
                  value={draft.approvalMode}
                  onChange={setApprovalMode}
                  options={APPROVAL_MODE_CARDS}
                />
              </CardBody>
            </SettingsCard>
          )}

          <SettingsCard aria-labelledby="settings-tool-loading">
            <SettingsCardTitle
              id="settings-tool-loading"
              title="Tool loading"
              description="How Flora gets its tools when a chat starts."
            />
            <CardBody>
              <RadioCardGroup
                label="Tool loading"
                value={draft.toolLoading}
                onChange={setToolLoading}
                options={TOOL_LOADING_CARDS}
              />
            </CardBody>
          </SettingsCard>

          <ResetRow>
            <IconSlot aria-hidden="true">
              <Icon name="restart_alt" size={18} color="var(--ink-light)" />
            </IconSlot>
            <span>Want a clean slate?</span>
            <Button variant="subtle" size="sm" onClick={handleReset}>
              Reset tools and approvals to defaults
            </Button>
          </ResetRow>
        </>
      )}
    </SettingsSectionShell>
  );
}

const CardBody = styled.div`
  padding: 16px 24px 24px;
`;

const ResetRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: var(--font-size-small);
  color: var(--ink-light);
`;

const IconSlot = styled.span`
  display: inline-flex;
`;
